import {
  createContext,
  FC,
  MutableRefObject,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useAutoFocus from 'src/hooks/useAutoFocus';
import useCompany from 'src/hooks/useCompany';
import useGuideMe from 'src/hooks/useGuideMe';
import useRedirect from 'src/hooks/useRedirect';
import useStep from 'src/hooks/useStep';
import { IDisableStep, IModalData } from 'src/pages/EnterReceipts';
import { guideMeSteps } from 'src/sections/enter_receipt/GuideMeSteps';
import { Company } from 'src/types/Companies';
import { SetState } from 'src/types/generic';
import { IOfflineSupplier } from 'src/types/OfflineSupplier';
import { IPO } from 'src/types/PO';
import { IReceipt } from 'src/types/Receipt';
interface SearchPO {
  value: string;
  disable: boolean;
}
interface EnterReceiptContextType {
  currentStep: number;
  poNumber: string;
  selectedCompany: Company;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: SetState<number>;
  searchPo: SearchPO;
  setSearchPo: SetState<SearchPO>;
  supplierSearch: string;
  setSupplierSearch: SetState<string>;
  lineItemSearch: string;
  setLineItemSearch: SetState<string>;
  selectedReceiptPoRow: IPO | null;
  setSelectedReceiptPoRow: SetState<IPO | null>;
  selectedReceiptRow: IReceipt | null;
  setSelectedReceiptRow: SetState<IReceipt | null>;
  disableStep: IDisableStep;
  setDisableStep: SetState<IDisableStep>;
  guideMeModalData: IModalData[];
  setGuideMeModalData: SetState<IModalData[]>;
  openReceiptModal: boolean;
  setOpenReceiptModal: SetState<boolean>;
  openPoCloseModal: boolean;
  setOpenPoCloseModal: SetState<boolean>;
  showPreview: boolean;
  setShowPreview: SetState<boolean>;
  guideMe: JSX.Element;
  openGuideMe: () => void;
  toggleReceiptModal: () => void;
  togglePoCloseModal: () => void;
  toggleExistingAttachmentsModal: () => void;
  nextStep: () => void;
  openExistingAttachmentsModal: boolean;
  poTotal: number;
  setPoTotal: SetState<number>;
  selectedSupplierReceiptRow: IOfflineSupplier;
  setSelectedReceiptSupplierRow: SetState<IOfflineSupplier>;
  changeType: string;
  setChangeType: SetState<string>;
  buttonRef: MutableRefObject<HTMLButtonElement | null>;
}

export const EnterReceiptContext = createContext<EnterReceiptContextType>(
  {} as EnterReceiptContextType
);

interface EnterReceiptContextProps {
  children: ReactNode;
}

const EnterReceiptContextProvider: FC<EnterReceiptContextProps> = ({ children }) => {
  // search params state
  const [searchParams] = useSearchParams();
  const poNumber = useMemo(() => searchParams.get('poNumber') || '', [searchParams]);

  // step state
  const [currentStep, helpers] = useStep(2);
  const { goToNextStep, goToPrevStep, setStep } = helpers;

  // search state
  const [searchPo, setSearchPo] = useState<{ value: string; disable: boolean }>(
    poNumber
      ? { value: poNumber, disable: true }
      : {
          value: '',
          disable: false,
        }
  );
  const [poTotal, setPoTotal] = useState<number>(0);
  const [supplierSearch, setSupplierSearch] = useState<string>('');
  const [lineItemSearch, setLineItemSearch] = useState<string>('');
  const [changeType, setChangeType] = useState('');
  const { getUrl, redirect } = useRedirect();

  // authenticate user state
  const { user } = useAuth();
  const { company: selectedCompany } = useCompany();

  // selected grid row state
  const [selectedReceiptPoRow, setSelectedReceiptPoRow] = useState<IPO | null>(null);
  const [selectedReceiptRow, setSelectedReceiptRow] = useState<IReceipt | null>(null);

  const [disableStep, setDisableStep] = useState<IDisableStep>({
    disableNext: false,
    disablePrev: false,
  });
  const buttonRef = useAutoFocus(!!selectedReceiptPoRow?.selected && poTotal > 1, [
    currentStep,
    selectedReceiptRow?.selected,
  ]);
  // guide me wizard
  const guideId = useMemo(
    () => `enterReceiptGuide-${user.ClientId}-step-${currentStep}`,
    [currentStep, user.ClientId]
  );
  const [guideMeModalData, setGuideMeModalData] = useState<IModalData[]>(guideMeSteps);
  const [openReceiptModal, setOpenReceiptModal] = useState<boolean>(false);
  const [openPoCloseModal, setOpenPoCloseModal] = useState<boolean>(false);
  const [openExistingAttachmentsModal, setOpenExistingAttachmentsModal] = useState(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const { guideMe, openGuideMe } = useGuideMe({
    steps: guideMeModalData,
    guideId: guideId,
    focusRef: buttonRef,
  });

  const [selectedSupplierReceiptRow, setSelectedReceiptSupplierRow] = useState<IOfflineSupplier>(
    {} as IOfflineSupplier
  );

  const toggleReceiptModal = useCallback(() => {
    setOpenReceiptModal((prev) => !prev);
  }, []);

  const togglePoCloseModal = useCallback(() => {
    setOpenPoCloseModal((prev) => !prev);
  }, []);

  const toggleExistingAttachmentsModal = useCallback(() => {
    setOpenExistingAttachmentsModal((prev) => !prev);
  }, []);

  const nextStep = useCallback(() => {
    const poStatus = selectedReceiptPoRow?.poStatus === 'Open';
    const selectPo = selectedReceiptPoRow?.selected;
    const closedReceipt = selectedReceiptRow?.receiptStatus === 'Completed';
    const isDeletedReceipt = selectedReceiptRow?.receiptStatus === 'Deleted';

    if (currentStep === 1 && !selectPo) {
      toggleReceiptModal();
      return;
    } else if (currentStep === 2 && poStatus) {
      toggleExistingAttachmentsModal();
      return;
    } else if (currentStep === 2 && poStatus && isDeletedReceipt) {
      redirect(
        getUrl('receiptDetails')(
          selectedReceiptRow?.receivingReceiptId.toString(),
          selectedReceiptRow?.poId?.toString()
        )
      );
    } else if (currentStep === 2 && (!poStatus || closedReceipt)) {
      togglePoCloseModal();
      return;
    }
    goToNextStep();
  }, [
    selectedReceiptPoRow?.poStatus,
    selectedReceiptPoRow?.selected,
    selectedReceiptRow?.receiptStatus,
    selectedReceiptRow?.receivingReceiptId,
    selectedReceiptRow?.poId,
    currentStep,
    goToNextStep,
    toggleReceiptModal,
    redirect,
    getUrl,
    toggleExistingAttachmentsModal,
    togglePoCloseModal,
  ]);
  return (
    <EnterReceiptContext.Provider
      value={{
        currentStep,
        poNumber,
        selectedCompany,
        goToNextStep,
        goToPrevStep,
        setStep,
        searchPo,
        setSearchPo,
        supplierSearch,
        setSupplierSearch,
        lineItemSearch,
        setLineItemSearch,
        selectedReceiptPoRow,
        setSelectedReceiptPoRow,
        selectedReceiptRow,
        setSelectedReceiptRow,
        disableStep,
        setDisableStep,
        guideMeModalData,
        setGuideMeModalData,
        openReceiptModal,
        setOpenReceiptModal,
        openPoCloseModal,
        setOpenPoCloseModal,
        showPreview,
        setShowPreview,
        guideMe,
        openGuideMe,
        openExistingAttachmentsModal,
        toggleReceiptModal,
        togglePoCloseModal,
        toggleExistingAttachmentsModal,
        nextStep,
        poTotal,
        setPoTotal,
        selectedSupplierReceiptRow,
        setSelectedReceiptSupplierRow,
        changeType,
        setChangeType,
        buttonRef,
      }}
    >
      {children}
    </EnterReceiptContext.Provider>
  );
};

export default EnterReceiptContextProvider;
